import { env } from '@/env.js';

/** A game's Tile from the art library, as svc-game stores it in `metadata.tile`. */
export interface GameTile {
  url: string;
  kitVersion: number;
}

function libraryBase(origin: string): string {
  return origin.replace(/\/+$/, '');
}

/**
 * The tile a card can show, or null when the stored value is missing or not an
 * https file of the art library; null keeps the card on its stand-in. A tile
 * replaces the name on the card, so a URL that might not load is never trusted.
 */
export function parseGameTile(value: unknown, origin: string = env.VITE_IMG_ORIGIN): GameTile | null {
  if (typeof value !== 'object' || value === null) return null;
  const { url, kitVersion } = value as Record<string, unknown>;
  if (typeof url !== 'string' || url.length > 1024 || typeof kitVersion !== 'number') return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' || !parsed.href.startsWith(`${libraryBase(origin)}/`)) return null;
  } catch {
    return null;
  }
  return { url, kitVersion };
}

/**
 * The theme card a game shows at `now`, or null when none is due. `value` is
 * the raw `metadata.themeTiles` (theme code → `{ url, kitVersion, startsAt,
 * endsAt }`): an entry counts while `startsAt <= now < endsAt`, its URL passing
 * the same check as {@link parseGameTile}. When windows overlap, the theme that
 * started last wins, so a short promotion inside a long season shows. Windows
 * are instants, compared against the player's clock.
 */
export function pickThemeTile(value: unknown, now: Date, origin: string = env.VITE_IMG_ORIGIN): GameTile | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const at = now.getTime();
  let best: { tile: GameTile; start: number } | null = null;
  for (const entry of Object.values(value as Record<string, unknown>)) {
    const tile = parseGameTile(entry, origin);
    if (!tile) continue;
    const { startsAt, endsAt } = entry as Record<string, unknown>;
    const start = typeof startsAt === 'string' ? Date.parse(startsAt) : Number.NaN;
    const end = typeof endsAt === 'string' ? Date.parse(endsAt) : Number.NaN;
    if (!(start <= at && at < end)) continue;
    if (!best || start > best.start) best = { tile, start };
  }
  return best?.tile ?? null;
}

/**
 * The picture a card draws for its game at `now`: a theme card due now, else
 * the game's Tile, else null (the stand-in).
 */
export function cardTile(
  game: { tile?: unknown; themeTiles?: unknown },
  now: Date,
  origin: string = env.VITE_IMG_ORIGIN,
): GameTile | null {
  return pickThemeTile(game.themeTiles, now, origin) ?? parseGameTile(game.tile, origin);
}

/**
 * CSS width a lobby card's tile is resized for. The cards run from about 150px
 * (portrait) to 184px (poster), and grids stretch a little past that.
 */
export const TILE_CSS_WIDTH = 240;

export interface TileImage {
  src: string;
  srcSet: string;
}

/**
 * The tile through Cloudflare Image Transformations at the image origin, at the
 * card's width for 1x and 2x screens. `tile` comes from {@link parseGameTile},
 * so it is a file under the origin.
 */
export function tileImage(tile: GameTile, origin: string = env.VITE_IMG_ORIGIN): TileImage {
  const base = libraryBase(origin);
  return resized(base, tile.url.slice(base.length + 1), TILE_CSS_WIDTH);
}

/**
 * Any art-library file resized to `cssWidth` for 1x and 2x screens, or null
 * when the URL is not under the image origin (Transformations only run there).
 */
export function libraryImage(url: string, cssWidth: number, origin: string = env.VITE_IMG_ORIGIN): TileImage | null {
  const base = libraryBase(origin);
  if (!url.startsWith(`${base}/`)) return null;
  return resized(base, url.slice(base.length + 1), cssWidth);
}

function resized(base: string, path: string, cssWidth: number): TileImage {
  const at = (dpr: number) => `${base}/cdn-cgi/image/width=${cssWidth},dpr=${dpr},format=auto,quality=85/${path}`;
  return { src: at(1), srcSet: `${at(1)} 1x, ${at(2)} 2x` };
}
